import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  cn,
} from '@pantry/ui';
import { useProductGroups, useAddGroupMember } from '@pantry/api-client';
import { FolderPlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';

interface AddToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  productName: string;
  householdId: string;
}

/**
 * Dialog for adding an ungrouped product to an existing product group.
 */
export function AddToGroupDialog({
  open,
  onOpenChange,
  productId,
  productName,
  householdId,
}: AddToGroupDialogProps) {
  const { t } = useLingui();
  const [search, setSearch] = useState('');

  const { data: groups, isLoading } = useProductGroups(householdId);
  const addGroupMember = useAddGroupMember();

  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);

  const filteredGroups = (groups ?? []).filter((g) =>
    g.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelect = async (groupId: string, groupName: string) => {
    try {
      await addGroupMember.mutateAsync({ householdId, groupId, productId });
      toast.success(t`"${productName}" added to "${groupName}"`);
      onOpenChange(false);
    } catch {
      toast.error(t`Failed to add product to group`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <FolderPlusIcon className="h-5 w-5 text-primary" />
            </div>
            <Trans>Add {productName} to a group</Trans>
          </DialogTitle>
          <DialogDescription className="pt-1">
            <Trans>Select a group to add this product to.</Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            type="search"
            placeholder={t`Search groups…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-xl"
            autoFocus
          />

          <div className="rounded-xl border border-border max-h-64 overflow-y-auto divide-y divide-border/50">
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                <Trans>Loading groups…</Trans>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                {search ? (
                  <Trans>No groups match &ldquo;{search}&rdquo;</Trans>
                ) : (
                  <Trans>No groups found</Trans>
                )}
              </div>
            ) : (
              filteredGroups.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => void handleSelect(group.id, group.name)}
                  disabled={addGroupMember.isPending}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors first:rounded-t-xl last:rounded-b-xl',
                    'focus-visible:outline-none focus-visible:bg-muted/40',
                    addGroupMember.isPending && 'pointer-events-none opacity-60'
                  )}
                >
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-muted/50 text-base">
                    {group.icon ?? '📦'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground">
                      {group.name}
                    </span>
                    {group.category && (
                      <span className="block truncate text-xs text-muted-foreground">
                        {group.category}
                      </span>
                    )}
                  </span>
                  <span className="flex-shrink-0 text-xs text-muted-foreground tabular-nums">
                    <Trans>{group.memberCount} items</Trans>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={addGroupMember.isPending}
            className="rounded-xl"
          >
            <Trans context="action_button">Cancel</Trans>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
